import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActiveUserConnectorService, ILoginResponse, IUpdatePortraitRequest, IUpdateUserRequest_, Stamp, UserModel, UserPortrait } from '@core/api/data-access';
import { StorageKeys } from '@core/storage';
import { SignalStore } from '@feature-signal-store';
import { StoredFile } from '@global-data';
import { TranslateService } from '@ngx-translate/core';
import { setUser } from '@sentry/angular';
import { ToastService } from '@utils';
import { catchError, filter, forkJoin, map, Observable, of, shareReplay, take } from 'rxjs';
import { useDevtools, useStorePersistence } from 'signalstory';
import { ActiveUserService } from '../services/active-user.service';
import { activeUserInitialState, IActiveUserState } from './active-user.state';

@Injectable({
	providedIn: 'root',
})
export class ActiveUserStore extends SignalStore<IActiveUserState> {
	private readonly activeUserConnectorService = inject(ActiveUserConnectorService);
	private readonly toastService = inject(ToastService);
	private readonly translateService = inject(TranslateService); // Assuming TranslateService is provided in the app

	public readonly isAuthenticated = computed(() => {
		const connectorAuth = this.activeUserConnectorService.isAuthenticated(); // its a signal that returns a boolean only after login process from /login
		return connectorAuth;
	});
	private readonly destroyRef = inject(DestroyRef);
	/** -------------------------- USER -------------------------- */
	public readonly id$ = computed(() => this.state()?.id);
	public readonly user$ = computed(() => {
		const stateUser = this.state()?.user;
		return stateUser instanceof UserModel ? stateUser : new UserModel(stateUser);
	});
	public readonly portrait$ = computed(() => this.state()?.portrait);
	public readonly portraitStoredFile$ = computed(() => {
		if (!this.portrait$()) return null;
		return new StoredFile(this.portrait$()?.image, this.portrait$()?.imageType);
	});
	/** -------------------------- Stamps -------------------------- */
	public readonly stamps$ = computed(() => this.state()?.stamps);
	public readonly defaultStamp$ = computed(() => {
		const user = this.user$();
		return user?.preferences?.defaultStamp || 0;
	});
	/** -------------------------- Observables -------------------------- */
	public readonly activeUserObs$: Observable<UserModel> = toObservable(this.user$).pipe(filter(Boolean));
	private readonly activeUserService: ActiveUserService = inject(ActiveUserService);

	constructor() {
		super('Active User Store', 'Global', activeUserInitialState, {
			plugins: [
				useStorePersistence({
					persistenceKey: StorageKeys.Stores.ACTIVE_USER_STORE,
					persistenceStorage: localStorage,
				}),
			],
		});

		this.activeUserConnectorService.activeUserLoggedIn$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
			if (response) {
				const { authResponse, jwt } = response;
				this.updateActiveUserState(authResponse.user, false);
				this.setSentryUser(this.user$(), authResponse);
			}
		});
		this.activeUserConnectorService.activeUserLoggedOut$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.set(activeUserInitialState);
		});
	}

	public updateActiveUserState(user: UserModel, updateBackend: boolean = false) {
		this.update(state => {
			return {
				...state,
				id: user.id,
				user: user,
			};
		});
		if (updateBackend) {
			this.activeUserService.updateUser({ user, password: '' }).subscribe(response => {
				console.log(`[ActiveUserStore.updateActiveUserState] User updated:`, response);
			});
		}
	}

	private setSentryUser(user: UserModel, response: ILoginResponse) {
		// Set the user in Sentry:
		setUser({
			id: user?.id,
			username: user?.username,
			ip_address: '{{auto}}',
			email: user?.email,
			// Custom user data:
			phone_number: user?.phonenumber,
			company: response?.tenant,
			user_roles: user?.roles,
			enabled: user.enabled,
			fullName: user.fullname,
			phone: user.phonenumber,
			language: user?.preferences?.language,
			roles: user.roles,
			tags: user.tags,
		});
	}

	/** --------------------------- HTTP -------------------------- */

	public readonly User = {
		load: (force = false) => {
			const id = this.id$();
			if (!id) return of(undefined);
			if (!force && this.user$()) return of(this.user$());
			const userRequest$ = this.activeUserService.getUser(id).pipe(
				shareReplay(1),
				map(user => {
					if (user) {
						this.update(
							state => ({
								...state,
								user: user.user,
							}),
							'Load User',
						);
					}
					return user;
				}),
			);
			userRequest$.subscribe();
			return userRequest$;
		},
		update: (request: IUpdateUserRequest_) => {
			const id = this.id$() || request?.user?.id;
			if (!id) return of(undefined);
		},
	};
	public readonly Portrait = {
		load: (force = false) => {
			const id = this.id$();
			if (!id) return of(undefined);
			if (!force && this.portrait$()) return of(this.portrait$());
			if (this.user$()?.hasPortrait === false) return of(undefined);
			const portraitRequest$: Observable<UserPortrait> = this.activeUserService.getPortrait(id).pipe(
				shareReplay(1),
				map(portrait => {
					if (portrait) {
						this.update(
							state => ({
								...state,
								portrait: new UserPortrait(portrait),
							}),
							'Load Portrait',
						);
					}
					return portrait;
				}),
			);
			portraitRequest$.subscribe();
			return portraitRequest$ as Observable<UserPortrait>;
		},
		update: (updatedPortrait: UserPortrait) => {
			const request: IUpdatePortraitRequest = {
				userId: this.id$(),
				portraitData: updatedPortrait.image,
				portraitType: updatedPortrait.imageType,
			};
			const update$ = this.activeUserService.updatePortrait(request).pipe(
				shareReplay(1),
				map((response: IUpdatePortraitRequest) => {
					if (response) {
						this.update(
							state => ({
								...state,
								portrait: new UserPortrait({
									image: response.portraitData,
									imageType: response.portraitType,
								}),
							}),
							'Update Portrait',
						);
					}
					return response;
				}),
				catchError(error => {
					console.error('Error updating portrait:', error);
					return of(undefined);
				}),
			);
			update$.subscribe();
			return update$;
		},
		delete: () => {
			const id = this.id$();
			if (!id) return of(undefined);
			const delete$ = this.activeUserService.deletePortrait(id).pipe(
				shareReplay(1),
				map(() => {
					this.update(
						state => ({
							...state,
							portrait: null,
						}),
						'Delete Portrait',
					);
				}),
				catchError(error => {
					console.error('Error deleting portrait:', error);
					return of(undefined);
				}),
			);
			delete$.subscribe();
			return delete$;
		},
	};
	public readonly Stamps = {
		load: (force = false): Observable<{ stamps: Stamp[]; defaultStamp: number }> => {
			const id = this.id$();
			if (!id) return of(undefined);
			if (!force && this.stamps$()) return of({ stamps: this.stamps$(), defaultStamp: this.defaultStamp$() });
			const stampsRequest$ = this.activeUserService.getStamps(id).pipe(
				shareReplay(1),
				map(stamps => {
					const stampsModel = stamps.stamps.map((stamp, index) => new Stamp(stamp, index + 2));
					if (stampsModel) {
						this.update(
							state => ({
								...state,
								stamps: stampsModel,
							}),
							'Load Stamps',
						);
					}
					return {
						stamps: stampsModel,
						defaultStamp: this.defaultStamp$(),
					};
				}),
			);
			stampsRequest$.subscribe();
			return stampsRequest$;
		},
		create: (stamp: Stamp) => {
			const id = this.id$();
			if (!id) return of(undefined);
			const stampAndId: Stamp & { userId: string } = { ...stamp, userId: id };
			if (this.stamps$()?.some(existingStamp => existingStamp.name === stamp.name)) {
				this.toastService.error('user.form.section.stamps.stampNameExists');
				return of(undefined);
			}
			return this.activeUserService.createStamp(stampAndId).pipe(
				take(1),
				map(() => {
					if (stamp) {
						this.update(state => {
							state.stamps.push(new Stamp(stamp, state.stamps.length + 2));
							state.stamps = [...state.stamps];
							return { ...state };
						});
					}
					this.toastService.success(this.translateService.instant('user.form.section.stamps.stampCreated', { stampName: stamp.name }));
					return stamp;
				}),
				catchError(error => {
					if (error?.error?.error?.includes('name exists')) {
						this.toastService.error('user.form.section.stamps.stampNameExists');
					} else console.error('Error creating stamp:', error);
					return of(undefined);
				}),
			);
		},
		delete: (stamp: Stamp) => {
			const id = this.id$();
			if (!id) return of(undefined);
			return this.activeUserService.deleteStamp(id, stamp.name).pipe(
				shareReplay(1),
				map(() => {
					this.update(
						state => ({
							...state,
							stamps: state.stamps.filter(existingStamp => existingStamp.name !== stamp.name),
						}),
						'Delete Stamp',
					);
					this.toastService.success(this.translateService.instant('user.form.section.stamps.stampDeleted', { stampName: stamp.name }));
					return stamp;
				}),
				catchError(error => {
					console.error('Error deleting stamp:', error);
					return of(undefined);
				}),
			);
		},
		update: (stamp: Stamp) => {
			// update is combination of delete and create
			const id = this.id$();
			if (!id) return of(undefined);
			const delete$ = this.Stamps.delete(stamp);
			const create$ = this.Stamps.create(stamp);
			return forkJoin([delete$, create$]).pipe(
				map(() => {
					return stamp;
				}),
			);
		},
		setDefaultStamp: (stamp: Stamp) => {
			const id = this.id$();
			if (!id) return of(undefined);
			// update the user preferences.defaultStamp
			const preferences = this.user$()?.preferences;
			if (!preferences) return of(undefined);
			preferences.defaultStamp = stamp.index + 2;
			return this.activeUserService.updateUser({ user: this.user$(), password: '' }).pipe(
				shareReplay(1),
				map((response: IUpdateUserRequest_) => {
					if (response) {
						this.update(state => ({
							...state,
							user: response.user,
						}));
					}
					this.toastService.success(this.translateService.instant('user.form.section.stamps.defaultStampSet', { stampName: stamp.name }));
					return response;
				}),
			);
		},
	};
}
